<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';

  let googleMapsLink = '';
  let name = '';
  let address = '';
  let selectedCategories = [];
  
  let allCategories = [];
  let claimedPremises = [];
  
  let isLoading = false;
  let error = '';
  let successMessage = '';
  let currentUser = null;

  onMount(async () => {
    const unsubscribe = authStore.subscribe(value => {
      currentUser = value.user;
    });

    if (currentUser) {
      fetchPremiseCategories();
      fetchUserPremises();
    }
    
    return unsubscribe;
  });

  async function fetchPremiseCategories() {
    try {
      const response = await fetch(getApiUrl('/premises/categories'));
      if (!response.ok) throw new Error('Could not fetch categories.');
      allCategories = await response.json();
    } catch (e) {
      error = e.message;
    }
  }

  async function fetchUserPremises() {
    isLoading = true;
    try {
      const userId = currentUser?.idTokenClaims?.oid;
      const response = await fetch(getApiUrl(`/premises/user/${userId}`));
      if (!response.ok) throw new Error('Could not fetch your premises.');
      claimedPremises = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  async function handleSubmit() {
    isLoading = true;
    error = '';
    successMessage = '';

    if (!name || !address || !googleMapsLink || selectedCategories.length === 0) {
      error = 'All fields are required.';
      isLoading = false;
      return;
    }

    const payload = {
      userId: currentUser?.idTokenClaims?.oid,
      name,
      address,
      googleMapsLink,
      categories: selectedCategories,
    };

    try {
      const response = await fetch(getApiUrl('/premises'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to add premise.');
      }
      
      successMessage = 'Premise successfully claimed!';
      // Reset form
      name = '';
      address = '';
      googleMapsLink = '';
      selectedCategories = [];
      // Refresh the list
      fetchUserPremises();

    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }
</script>

<div class="management-container">
  <h2>Premise Management</h2>
  
  <div class="form-section">
    <h3>Claim a New Premise</h3>
    <p>Enter the premise details below. The name and address can often be retrieved from the Google Maps link.</p>
    <form on:submit|preventDefault={handleSubmit}>
      <div class="form-group">
        <label for="google-maps-link">Google Maps Hyperlink</label>
        <input type="url" id="google-maps-link" bind:value={googleMapsLink} placeholder="https://maps.app.goo.gl/..." />
      </div>
      <div class="form-group">
        <label for="name">Premise Name</label>
        <input type="text" id="name" bind:value={name} />
      </div>
      <div class="form-group">
        <label for="address">Address</label>
        <input type="text" id="address" bind:value={address} />
      </div>
      <div class="form-group">
        <label>Categories</label>
        <div class="category-checkboxes">
          {#each allCategories as category}
            <div class="checkbox-item">
              <input type="checkbox" id="cat-{category}" value={category} bind:group={selectedCategories} />
              <label for="cat-{category}">{category}</label>
            </div>
          {/each}
        </div>
      </div>
      <button type="submit" disabled={isLoading}>
        {#if isLoading}Claiming...{:else}Claim Premise{/if}
      </button>
      {#if error}<p class="error">{error}</p>{/if}
      {#if successMessage}<p class="success">{successMessage}</p>{/if}
    </form>
  </div>

  <div class="list-section">
    <h3>My Claimed Premises</h3>
    {#if isLoading}
      <p>Loading premises...</p>
    {:else if claimedPremises.length === 0}
      <p>You have not claimed any premises yet.</p>
    {:else}
      <div class="premise-list">
        {#each claimedPremises as premise (premise._id)}
          <div class="premise-card">
            <h4>{premise.name}</h4>
            <p>{premise.address}</p>
            <p class="categories">
              {#each premise.categories as category}
                <span>{category}</span>
              {/each}
            </p>
            <a href={premise.googleMapsLink} target="_blank" rel="noopener noreferrer">View on Map</a>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .management-container {
    display: grid;
    grid-template-columns: 1fr;
    gap: 2rem;
    max-width: 1200px;
    margin: 2rem auto;
  }

  @media (min-width: 992px) {
    .management-container {
      grid-template-columns: 1fr 2fr;
    }
  }

  .form-section, .list-section {
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
  }
  
  form { display: flex; flex-direction: column; gap: 1rem; }
  .form-group { margin-bottom: 1rem; }
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 1rem; box-sizing: border-box; }
  button { padding: 0.75rem; background-color: #007bff; color: white; border: none; border-radius: 4px; font-size: 1.1rem; cursor: pointer; }
  button:disabled { background-color: #aaa; }
  
  .category-checkboxes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.5rem;
    border: 1px solid #eee;
    padding: 1rem;
    border-radius: 4px;
  }

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  input[type="checkbox"] {
    width: 1.2em;
    height: 1.2em;
  }
  
  .premise-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }

  .premise-card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    background: #f8f9fa;
  }
  
  .premise-card h4 {
    margin-top: 0;
  }
  
  .premise-card .categories span {
    display: inline-block;
    background: #e9ecef;
    color: #495057;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    margin-right: 5px;
    margin-bottom: 5px;
  }
  
  .error { color: red; }
  .success { color: green; }
</style>
